import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection, query, where, getDocs, deleteDoc, doc, setDoc, addDoc,
  updateDoc, getDoc, orderBy, limit, Timestamp, documentId,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useApp } from '../App';
import { requireRole } from '../utils/roles';
import {
  EventTypes, AttendanceStatuses, RSVPResponses, positionLabel,
  ScheduleStatuses, scheduleStatusOf, withRo,
  DutyPresets, DutyStatuses, formatDate, formatTime, isToday, isPast, MediaServer,
} from '../utils/constants';
import { uploadMediaFile } from '../utils/mediaUpload';

const toWebUrl = (url) => {
  if (!url) return url;
  return url.replace('http://122.45.112.83:5000', MediaServer.baseURL);
};

// 출석 정렬 우선순위
const STATUS_ORDER = { attending: 0, late: 1, early_leave: 2, undecided: 3, not_attending: 4 };
// 참석으로 집계되는 상태 (iOS/Android AttendanceService.getAttendanceStats와 동일)
const PRESENT = ['attending', 'late', 'early_leave'];

// 라인업 기본 슬롯 (Android LineupService.createEmptyLineup과 동일 규격)
function createEmptySlots() {
  const slots = [];
  for (let line = 1; line <= 4; line++) {
    ['LW', 'C', 'RW'].forEach(pos => slots.push({
      id: `F${line}_${pos}`, position: pos, playerId: null, playerName: null,
      playerNumber: null, lineNumber: line, lineType: 'forward',
    }));
  }
  for (let line = 1; line <= 3; line++) {
    ['LD', 'RD'].forEach(pos => slots.push({
      id: `D${line}_${pos}`, position: pos, playerId: null, playerName: null,
      playerNumber: null, lineNumber: line, lineType: 'defense',
    }));
  }
  slots.push({ id: 'G1', position: 'G', playerId: null, playerName: null, playerNumber: null, lineNumber: 1, lineType: 'goalie' });
  slots.push({ id: 'G2', position: 'G', playerId: null, playerName: null, playerNumber: null, lineNumber: 2, lineType: 'goalie' });
  return slots;
}

// 듀티 문서 → 표시 라벨 (iOS는 title:"Puck", Android는 type:"puck")
function dutyLabel(d) {
  const raw = (d.type || d.title || '').toString();
  const key = raw.toLowerCase().replace(/\s+/g, '_');
  return DutyPresets[key] || { label: raw || '당번', icon: '📌' };
}

export default function SchedulePanel({ onCopySchedule }) {
  const { currentTeam, isStaff, isAdmin, roles, userProfile } = useApp();
  const canEdit = isStaff;   // 일정·출석·라인업·당번 편집 = 운영진. 게스트 승인은 isAdmin (W1-01/03)
  const [allSchedules, setAllSchedules] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('today');
  const [listStatusFilter, setListStatusFilter] = useState([]);
  const [listSort, setListSort] = useState(() => localStorage.getItem('ionice_sched_sort') || 'date');
  const [listAsc, setListAsc] = useState(() => localStorage.getItem('ionice_sched_asc') !== 'false');
  const [tab, setTab] = useState('info');

  // 팀 멤버 (팀 전환 시 1회 로드)
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // 일정별 참석 인원 요약 (목록 카드 배지)
  const [countMap, setCountMap] = useState({});
  const countedRef = useRef(new Set());   // 이미 집계한 일정 ID (중복 조회 방지)

  // 상세 탭 데이터
  const [attendances, setAttendances] = useState([]);
  const [attLoading, setAttLoading] = useState(false);
  const [lineup, setLineup] = useState(null);
  const [lineupDirty, setLineupDirty] = useState(false);
  const [lineupSaving, setLineupSaving] = useState(false);
  const [duties, setDuties] = useState([]);
  const [newDutyType, setNewDutyType] = useState('puck');
  const [program, setProgram] = useState(null);
  const [guestApps, setGuestApps] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // 미디어
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFileIndex, setUploadFileIndex] = useState({ current: 0, total: 0 });
  const [selectedMedia, setSelectedMedia] = useState(null);
  const fileInputRef = useRef(null);

  // ── 로딩 ────────────────────────────────────────────────────────────────
  // 일정은 팀당 1회만 읽고, 시점 필터는 클라이언트에서 처리 (읽기 절약)
  const loadSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'teams', currentTeam.id, 'schedules'), orderBy('date', 'asc'));
      const snap = await getDocs(q);
      setAllSchedules(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      countedRef.current = new Set();   // 새로 읽었으니 배지 캐시 초기화
      setCountMap({});
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [currentTeam]); // eslint-disable-line

  // 목록 카드 참석 배지: 아직 집계 안 한 일정만, 화면에 보이는 30건까지
  const loadCounts = async (list) => {
    const ids = list.map(s => s.id).filter(id => !countedRef.current.has(id)).slice(0, 30);
    if (ids.length === 0) return;
    ids.forEach(id => countedRef.current.add(id));
    try {
      const snap = await getDocs(query(collection(db, 'attendances'), where('scheduleId', 'in', ids)));
      const map = {};
      ids.forEach(id => { map[id] = { present: 0, total: 0 }; });
      snap.docs.forEach(d => {
        const a = d.data();
        if (!map[a.scheduleId]) map[a.scheduleId] = { present: 0, total: 0 };
        map[a.scheduleId].total += 1;
        if (PRESENT.includes(a.status)) map[a.scheduleId].present += 1;
      });
      setCountMap(prev => ({ ...prev, ...map }));
    } catch (err) { console.error('참석 집계 실패', err); }
  };

  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const snap = await getDocs(collection(db, 'teams', currentTeam.id, 'members'));
      const list = [];
      for (const md of snap.docs) {
        const mData = md.data();
        let profile = {};
        try {
          const ud = await getDoc(doc(db, 'users', md.id));
          if (ud.exists()) profile = ud.data();
        } catch { /* ignore */ }
        list.push({
          id: md.id,
          name: mData.name || profile.name || profile.displayName || md.id.substring(0, 6),
          jerseyNumber: mData.jerseyNumber ?? profile.jerseyNumber ?? null,
          position: mData.position || profile.position || null,
          role: mData.role || 'member',
        });
      }
      list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko'));
      setMembers(list);
    } catch (err) { console.error(err); }
    setMembersLoading(false);
  }, [currentTeam]);

  const loadAttendances = async (scheduleId) => {
    setAttLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'attendances'), where('scheduleId', '==', scheduleId)));
      let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // userName 누락 문서 보강 (Android AttendanceService와 동일 처리)
      const needName = [...new Set(list.filter(a => !a.userName).map(a => a.userId))].filter(Boolean);
      if (needName.length > 0) {
        const nameMap = {};
        for (let i = 0; i < needName.length; i += 30) {
          const chunk = needName.slice(i, i + 30);
          try {
            const us = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
            us.docs.forEach(u => { nameMap[u.id] = u.data().name || u.data().displayName; });
          } catch { /* ignore */ }
        }
        list = list.map(a => (!a.userName && nameMap[a.userId]) ? { ...a, userName: nameMap[a.userId] } : a);
      }
      setAttendances(list);
    } catch (err) { console.error(err); }
    setAttLoading(false);
  };

  // 게스트 신청 (guestApplications 컬렉션 — 출석 문서와 별도)
  const loadGuests = async (scheduleId) => {
    try {
      const snap = await getDocs(query(collection(db, 'guestApplications'), where('scheduleId', '==', scheduleId)));
      let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // applicantName 누락분 보강
      const needName = [...new Set(list.filter(g => !g.applicantName).map(g => g.applicantId))].filter(Boolean);
      if (needName.length > 0) {
        const nameMap = {};
        for (let i = 0; i < needName.length; i += 30) {
          const chunk = needName.slice(i, i + 30);
          try {
            const us = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
            us.docs.forEach(u => { nameMap[u.id] = u.data().name || u.data().displayName; });
          } catch { /* ignore */ }
        }
        list = list.map(g => (!g.applicantName && nameMap[g.applicantId]) ? { ...g, applicantName: nameMap[g.applicantId] } : g);
      }
      setGuestApps(list);
    } catch (err) { console.error('게스트 신청 로드 실패', err); }
  };

  const approveGuest = async (g, name) => {
    if (!requireRole(roles, 'isAdmin')) return;
    if (!window.confirm(`${name}님의 게스트 신청을 승인하시겠습니까?`)) return;
    try {
      await updateDoc(doc(db, 'guestApplications', g.id), {
        status: 'approved',
        processedBy: auth.currentUser?.uid || null,
        processedAt: Timestamp.now(),
      });
      setGuestApps(prev => prev.map(x => x.id === g.id ? { ...x, status: 'approved' } : x));
    } catch (err) { alert('승인 실패: ' + err.message); }
  };

  const loadDetail = async (scheduleId) => {
    setDetailLoading(true);
    setLineupDirty(false);
    try {
      // 라인업
      const lq = query(
        collection(db, 'teams', currentTeam.id, 'lineups'),
        where('scheduleId', '==', scheduleId), limit(1)
      );
      const lsnap = await getDocs(lq);
      if (!lsnap.empty) {
        const d = lsnap.docs[0];
        const data = d.data();
        setLineup({ id: d.id, ...data, slots: (data.slots && data.slots.length) ? data.slots : createEmptySlots() });
      } else {
        setLineup({ id: null, teamId: currentTeam.id, scheduleId, title: '기본 라인업', slots: createEmptySlots(), isSharedWithTeam: false });
      }

      // 당번
      const dsnap = await getDocs(collection(db, 'teams', currentTeam.id, 'schedules', scheduleId, 'duties'));
      setDuties(dsnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // 세션 프로그램
      const psnap = await getDocs(query(
        collection(db, 'teams', currentTeam.id, 'sessionPrograms'),
        where('scheduleId', '==', scheduleId), limit(1)
      ));
      setProgram(psnap.empty ? null : { id: psnap.docs[0].id, ...psnap.docs[0].data() });
    } catch (err) { console.error('상세 데이터 로드 실패', err); }
    setDetailLoading(false);
  };

  // 해당 일정의 미디어만 조회 (기존에는 팀 미디어 전체를 읽어 클라이언트에서 필터링)
  const loadScheduleMedia = async (scheduleId) => {
    setMediaLoading(true);
    setSelectedMedia(null);
    try {
      const col = collection(db, 'teams', currentTeam.id, 'media');
      const [byContext, bySchedule] = await Promise.all([
        getDocs(query(col, where('contextId', '==', scheduleId))),
        getDocs(query(col, where('scheduleId', '==', scheduleId))),
      ]);
      const merged = new Map();
      [...byContext.docs, ...bySchedule.docs].forEach(d => merged.set(d.id, { id: d.id, ...d.data() }));
      const list = [...merged.values()].sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      setMediaItems(list);
    } catch (err) { console.error(err); }
    setMediaLoading(false);
  };

  useEffect(() => { if (currentTeam) { loadSchedules(); loadMembers(); } }, [currentTeam]); // eslint-disable-line
  useEffect(() => {
    if (selected && currentTeam) {
      loadAttendances(selected.id);
      loadGuests(selected.id);
      loadDetail(selected.id);
      loadScheduleMedia(selected.id);
    } else {
      setAttendances([]); setGuestApps([]); setLineup(null); setDuties([]); setProgram(null); setMediaItems([]);
    }
  }, [selected]); // eslint-disable-line

  // ── 출석 ────────────────────────────────────────────────────────────────
  const attMap = {};
  attendances.forEach(a => { if (a.userId) attMap[a.userId] = a; });

  // 멤버 + 출석 문서만 있는 인원 + 게스트 신청자를 하나의 명단으로 병합
  const rowMap = new Map();
  members.forEach(m => rowMap.set(m.id, {
    userId: m.id, name: m.name, jerseyNumber: m.jerseyNumber, position: m.position,
    role: m.role, att: attMap[m.id] || null, isGuest: false, guest: null,
  }));
  attendances.forEach(a => {
    if (!a.userId || rowMap.has(a.userId)) return;
    rowMap.set(a.userId, {
      userId: a.userId, name: a.userName || a.userId.substring(0, 6),
      jerseyNumber: null, position: null, role: 'guest', att: a, isGuest: true, guest: null,
    });
  });
  guestApps.forEach(g => {
    const uid = g.applicantId;
    if (!uid) return;
    const ex = rowMap.get(uid);
    if (ex) {
      rowMap.set(uid, { ...ex, guest: g, position: ex.position || g.requestedPosition || null });
    } else {
      rowMap.set(uid, {
        userId: uid, name: g.applicantName || uid.substring(0, 6),
        jerseyNumber: null, position: g.requestedPosition || null,
        role: 'guest', att: attMap[uid] || null, isGuest: true, guest: g,
      });
    }
  });

  const rosterRows = [...rowMap.values()].sort((a, b) => {
    const sa = STATUS_ORDER[a.att?.status] ?? 3;
    const sb = STATUS_ORDER[b.att?.status] ?? 3;
    if (sa !== sb) return sa - sb;
    return (a.name || '').localeCompare(b.name || '', 'ko');
  });

  const activeRows = rosterRows.filter(r => !isInactiveGuest(r));
  const counts = {
    attending: activeRows.filter(r => r.att?.status === 'attending').length,
    late: activeRows.filter(r => r.att?.status === 'late').length,
    not_attending: activeRows.filter(r => r.att?.status === 'not_attending').length,
    undecided: activeRows.filter(r => !r.att || !['attending', 'not_attending', 'late', 'early_leave'].includes(r.att.status)).length,
  };
  const presentCount = activeRows.filter(r => PRESENT.includes(r.att?.status)).length;

  const setStatus = async (row, status) => {
    if (!selected) return;
    const existing = row.att;
    // 결정적 ID로 중복 방지 (iOS/Android와 동일 규칙: {scheduleId}_{userId})
    const docId = existing?.id || `${selected.id}_${row.userId}`;
    const now = Timestamp.now();
    const payload = {
      scheduleId: selected.id,
      teamId: currentTeam.id,
      userId: row.userId,
      userName: row.name,
      status,
      checkInMethod: 'manual',
      checkedBy: auth.currentUser?.uid || null,
      isSelfCheckIn: false,
      updatedAt: now,
    };
    if (!existing) payload.createdAt = now;
    try {
      await setDoc(doc(db, 'attendances', docId), payload, { merge: true });
      setAttendances(prev => {
        const idx = prev.findIndex(a => a.id === docId);
        if (idx >= 0) { const next = [...prev]; next[idx] = { ...next[idx], ...payload, id: docId }; return next; }
        return [...prev, { ...payload, id: docId }];
      });
      setCountMap(prev => ({ ...prev, [selected.id]: undefined })); // 배지 무효화
    } catch (err) { alert('출석 상태 변경 실패: ' + err.message); }
  };

  // 운영진 대리 RSVP 입력 (카톡 등 외부 확인분 반영)
  const setRsvp = async (row, response) => {
    if (!selected) return;
    const existing = row.att;
    const uid = auth.currentUser?.uid || null;
    const docId = existing?.id || `${selected.id}_${row.userId}`;
    const now = Timestamp.now();
    // 같은 값을 다시 누르면 해제
    const next = (existing?.rsvpResponse === response) ? null : response;
    const payload = {
      scheduleId: selected.id,
      teamId: currentTeam.id,
      userId: row.userId,
      userName: row.name,
      rsvpResponse: next,
      rsvpRespondedAt: next ? now : null,
      rsvpSetBy: next ? uid : null,           // 본인 응답과 대리 입력 구분용
      rsvpSetByName: next ? (userProfile?.name || null) : null,
      updatedAt: now,
    };
    if (!existing) { payload.status = 'undecided'; payload.createdAt = now; }
    try {
      await setDoc(doc(db, 'attendances', docId), payload, { merge: true });
      setAttendances(prev => {
        const idx = prev.findIndex(a => a.id === docId);
        if (idx >= 0) { const nx = [...prev]; nx[idx] = { ...nx[idx], ...payload, id: docId }; return nx; }
        return [...prev, { ...payload, id: docId }];
      });
    } catch (err) { alert('RSVP 입력 실패: ' + err.message); }
  };

  // ── 라인업 ──────────────────────────────────────────────────────────────
  const assignSlot = (slotId, userId) => {
    const p = rosterRows.find(r => r.userId === userId);
    setLineup(prev => ({
      ...prev,
      slots: prev.slots.map(s => s.id === slotId ? {
        ...s,
        playerId: userId || null,
        playerName: p ? p.name : null,
        playerNumber: p?.jerseyNumber ?? null,
      } : s),
    }));
    setLineupDirty(true);
  };

  const saveLineup = async () => {
    if (!lineup || !selected) return;
    setLineupSaving(true);
    try {
      const data = {
        teamId: currentTeam.id,
        scheduleId: selected.id,
        title: lineup.title || '기본 라인업',
        slots: lineup.slots.map(s => ({
          id: s.id, position: s.position ?? null, playerId: s.playerId ?? null,
          playerName: s.playerName ?? null, playerNumber: s.playerNumber ?? null,
          lineNumber: s.lineNumber, lineType: s.lineType,
        })),
        isSharedWithTeam: lineup.isSharedWithTeam ?? false,
        createdBy: auth.currentUser?.uid || '',
        updatedAt: Timestamp.now(),
      };
      if (lineup.id) {
        await setDoc(doc(db, 'teams', currentTeam.id, 'lineups', lineup.id), data, { merge: true });
      } else {
        const ref = await addDoc(collection(db, 'teams', currentTeam.id, 'lineups'), { ...data, createdAt: Timestamp.now() });
        setLineup(prev => ({ ...prev, id: ref.id }));
      }
      setLineupDirty(false);
    } catch (err) { alert('라인업 저장 실패: ' + err.message); }
    setLineupSaving(false);
  };

  // ── 당번 ────────────────────────────────────────────────────────────────
  const addDuty = async () => {
    if (!selected) return;
    const preset = DutyPresets[newDutyType];
    try {
      const ref = await addDoc(collection(db, 'teams', currentTeam.id, 'schedules', selected.id, 'duties'), {
        teamId: currentTeam.id,
        scheduleId: selected.id,
        type: preset.code,     // Android 호환
        title: preset.title,   // iOS 호환
        icon: 'checkmark.circle',
        assignedUserId: null,
        assignedUserName: null,
        isRecurring: false,
        status: 'unassigned',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      setDuties(prev => [...prev, {
        id: ref.id, type: preset.code, title: preset.title,
        assignedUserId: null, assignedUserName: null, status: 'unassigned',
      }]);
    } catch (err) { alert('당번 추가 실패: ' + err.message); }
  };

  const assignDuty = async (dutyId, userId) => {
    const p = rosterRows.find(r => r.userId === userId);
    try {
      await updateDoc(doc(db, 'teams', currentTeam.id, 'schedules', selected.id, 'duties', dutyId), {
        assignedUserId: userId || null,
        assignedUserName: p ? p.name : null,
        status: userId ? 'accepted' : 'unassigned',
        updatedAt: Timestamp.now(),
      });
      setDuties(prev => prev.map(d => d.id === dutyId
        ? { ...d, assignedUserId: userId || null, assignedUserName: p ? p.name : null, status: userId ? 'accepted' : 'unassigned' }
        : d));
    } catch (err) { alert('당번 지정 실패: ' + err.message); }
  };

  const removeDuty = async (dutyId) => {
    if (!window.confirm('이 당번을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'teams', currentTeam.id, 'schedules', selected.id, 'duties', dutyId));
      setDuties(prev => prev.filter(d => d.id !== dutyId));
    } catch (err) { alert('삭제 실패: ' + err.message); }
  };

  // 일정 수정
  const saveSchedule = async (payload) => {
    if (!selected) return false;
    try {
      await updateDoc(doc(db, 'teams', currentTeam.id, 'schedules', selected.id), {
        ...payload, updatedAt: Timestamp.now(),
      });
      const merged = { ...selected, ...payload };
      setSelected(merged);
      setAllSchedules(prev => prev.map(s => s.id === selected.id ? merged : s));
      return true;
    } catch (err) { alert('일정 수정 실패: ' + err.message); return false; }
  };

  // ── 일정 / 미디어 ───────────────────────────────────────────────────────
  const handleDelete = async (scheduleId) => {
    if (!window.confirm('이 일정을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'teams', currentTeam.id, 'schedules', scheduleId));
      setAllSchedules(prev => prev.filter(s => s.id !== scheduleId));
      if (selected?.id === scheduleId) setSelected(null);
    } catch (err) { alert('삭제 실패: ' + err.message); }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0 || !selected) return;
    setUploading(true);
    setUploadFileIndex({ current: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('video/') && !file.type.startsWith('image/')) { alert('지원하지 않는 파일: ' + file.name); continue; }
      try {
        setUploadProgress(0);
        setUploadFileName(file.name);
        setUploadFileIndex({ current: i + 1, total: files.length });
        const et = selected.eventType || '';
        const context = et.includes('game') ? 'game' : 'training';
        await uploadMediaFile(file, currentTeam.id, {
          context, contextId: selected.id,
          uploaderName: userProfile?.name || auth.currentUser?.email,
          onProgress: (pct) => setUploadProgress(pct),
        });
      } catch (err) { alert('업로드 실패 (' + file.name + '): ' + err.message); }
    }
    setUploading(false); setUploadProgress(0); setUploadFileName('');
    loadScheduleMedia(selected.id);
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('이 미디어를 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'teams', currentTeam.id, 'media', mediaId));
      setMediaItems(prev => prev.filter(m => m.id !== mediaId));
      if (selectedMedia?.id === mediaId) setSelectedMedia(null);
    } catch (err) { alert('삭제 실패'); }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + 'GB';
  };
  const formatDuration = (sec) => {
    if (!sec) return '';
    return `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
  };

  // 시점 필터 (클라이언트 처리 — Firestore 재조회 없음)
  const schedules = (() => {
    let list = allSchedules;
    if (filter === 'today') list = list.filter(s => isToday(s.date, s.endDate));
    else if (filter === 'upcoming') list = list.filter(s => !isPast(s.date, s.endDate) && !isToday(s.date, s.endDate));
    else if (filter === 'past') list = list.filter(s => isPast(s.date, s.endDate));
    return filter === 'past' ? [...list].reverse() : list;
  })();

  // 상태 필터 + 정렬
  const STATUS_SEQ = ['draft', 'open', 'closed', 'cancelled'];
  const listStatusCount = {};
  STATUS_SEQ.forEach(k => { listStatusCount[k] = schedules.filter(s => scheduleStatusOf(s) === k).length; });

  const visibleSchedules = schedules
    .filter(s => listStatusFilter.length === 0 || listStatusFilter.includes(scheduleStatusOf(s)))
    .slice()
    .sort((a, b) => {
      let v = 0;
      if (listSort === 'status') {
        v = STATUS_SEQ.indexOf(scheduleStatusOf(a)) - STATUS_SEQ.indexOf(scheduleStatusOf(b));
      } else if (listSort === 'title') {
        v = (a.title || '').localeCompare(b.title || '', 'ko');
      }
      if (v === 0) {
        const ta = a.date?.seconds ?? 0, tb = b.date?.seconds ?? 0;
        v = ta === tb ? 0 : (ta < tb ? -1 : 1);
      }
      return listAsc ? v : -v;
    });

  const toggleListStatus = (k) => setListStatusFilter(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  const changeListSort = (k) => { setListSort(k); localStorage.setItem('ionice_sched_sort', k); };
  const toggleListAsc = () => { const v = !listAsc; setListAsc(v); localStorage.setItem('ionice_sched_asc', String(v)); };

  // 화면에 실제로 보이는 일정만 참석 배지 집계
  useEffect(() => {
    if (!currentTeam || visibleSchedules.length === 0) return;
    loadCounts(visibleSchedules);
  }, [currentTeam, filter, allSchedules, listStatusFilter]); // eslint-disable-line

  const todaySchedules = visibleSchedules.filter(s => isToday(s.date, s.endDate));
  const otherSchedules = visibleSchedules.filter(s => !isToday(s.date, s.endDate));
  const filters = [
    { key: 'today', label: '오늘' }, { key: 'upcoming', label: '예정' },
    { key: 'past', label: '지난' }, { key: 'all', label: '전체' },
  ];

  const programItems = (program?.workoutItems || program?.items || [])
    .slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const dutyAssigned = duties.filter(d => d.assignedUserId).length;
  const lineupFilled = lineup ? lineup.slots.filter(s => s.playerId).length : 0;

  const tabs = [
    { key: 'info', label: '정보', badge: null },
    { key: 'attendance', label: '참석', badge: selected ? `${presentCount}/${activeRows.length}` : null },
    { key: 'lineup', label: '라인업', badge: selected ? `${lineupFilled}` : null },
    { key: 'duty', label: '당번', badge: selected ? `${dutyAssigned}/${duties.length}` : null },
    { key: 'program', label: '프로그램', badge: program ? '●' : null },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '330px 1fr 320px', gap: 16, maxWidth: 1700 }}>
      {/* ── 1열: 일정 목록 ── */}
      <div style={st.panel}>
        <div style={{ ...st.panelHeader, borderBottom: 'none', paddingBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={st.panelTitle}>📅 일정</h2>
            <button onClick={() => loadSchedules()} disabled={loading}
              style={{ padding: '3px 6px', borderRadius: 4, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 12, cursor: 'pointer', lineHeight: 1 }}
              title="새로고침">
              {loading ? '⏳' : '🔄'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {filters.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                style={{
                  ...st.filterBtn,
                  backgroundColor: filter === f.key ? (f.key === 'today' ? 'rgba(239,68,68,0.15)' : 'rgba(91,196,216,0.15)') : 'transparent',
                  color: filter === f.key ? (f.key === 'today' ? '#EF4444' : '#5BC4D8') : '#667788',
                }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* 상태 필터 + 정렬 */}
        <div style={{ padding: '0 12px 10px', borderBottom: '1px solid rgba(91,196,216,0.06)' }}>
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 6 }}>
            {STATUS_SEQ.map(k => {
              const o = ScheduleStatuses[k];
              const on = listStatusFilter.includes(k);
              return (
                <button key={k} onClick={() => toggleListStatus(k)} title={o.desc} style={{
                  padding: '3px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${on ? o.color : 'rgba(255,255,255,0.06)'}`,
                  backgroundColor: on ? `${o.color}1F` : 'transparent',
                  color: on ? o.color : '#667788',
                }}>{o.label} {listStatusCount[k] || 0}</button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <select value={listSort} onChange={e => changeListSort(e.target.value)}
              style={{ ...st.select, flex: 1, fontSize: 11, padding: '4px 6px' }}>
              <option value="date">날짜순</option>
              <option value="status">상태순</option>
              <option value="title">제목순</option>
            </select>
            <button onClick={toggleListAsc} title={listAsc ? '오름차순' : '내림차순'} style={{
              padding: '4px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              border: '1px solid rgba(91,196,216,0.15)', backgroundColor: 'transparent', color: '#5BC4D8',
            }}>{listAsc ? '↑' : '↓'}</button>
            {listStatusFilter.length > 0 && (
              <button onClick={() => setListStatusFilter([])} style={{
                padding: '4px 8px', borderRadius: 6, fontSize: 10, cursor: 'pointer',
                border: 'none', backgroundColor: 'rgba(255,255,255,0.05)', color: '#8899AA',
              }}>해제</button>
            )}
          </div>
        </div>
        <div style={st.panelBody}>
          {loading ? <div style={st.empty}>로딩 중...</div> : visibleSchedules.length === 0 ? (
            <div style={st.empty}>
              <div style={{ fontSize: 36, marginBottom: 8, opacity: 0.5 }}>📅</div>
              {listStatusFilter.length > 0 ? '조건에 맞는 일정이 없습니다'
                : filter === 'today' ? '오늘 일정이 없습니다' : '일정이 없습니다'}
            </div>
          ) : (
            <>
              {filter === 'all' && todaySchedules.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, padding: '0 4px' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#EF4444' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#EF4444' }}>오늘</span>
                  </div>
                  {todaySchedules.map(sc => <ScheduleCard key={sc.id} schedule={sc} selected={selected} count={countMap[sc.id]} onClick={() => setSelected(sc)} />)}
                </div>
              )}
              {filter === 'all' && otherSchedules.length > 0 && (
                <div>
                  {todaySchedules.length > 0 && <div style={{ fontSize: 12, fontWeight: 600, color: '#778899', padding: '0 4px', marginBottom: 6 }}>기타</div>}
                  {otherSchedules.map(sc => <ScheduleCard key={sc.id} schedule={sc} selected={selected} count={countMap[sc.id]} onClick={() => setSelected(sc)} />)}
                </div>
              )}
              {filter !== 'all' && visibleSchedules.map(sc => <ScheduleCard key={sc.id} schedule={sc} selected={selected} count={countMap[sc.id]} onClick={() => setSelected(sc)} />)}
            </>
          )}
        </div>
      </div>

      {/* ── 2열: 상세 (탭) ── */}
      <div style={st.panel}>
        <div style={st.panelHeader}>
          <h2 style={st.panelTitle}>📋 {selected ? selected.title : '상세 정보'}</h2>
          {selected && detailLoading && <span style={{ fontSize: 11, color: '#667788' }}>불러오는 중...</span>}
        </div>

        {selected && (
          <div style={{ display: 'flex', gap: 4, padding: '8px 12px', borderBottom: '1px solid rgba(91,196,216,0.06)', flexWrap: 'wrap' }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '5px 12px', borderRadius: 6, border: 'none', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                backgroundColor: tab === t.key ? 'rgba(91,196,216,0.15)' : 'transparent',
                color: tab === t.key ? '#5BC4D8' : '#667788',
              }}>
                {t.label}
                {t.badge && <span style={{ marginLeft: 5, fontSize: 10, color: tab === t.key ? '#5BC4D8' : '#556677' }}>{t.badge}</span>}
              </button>
            ))}
          </div>
        )}

        <div style={st.panelBody}>
          {!selected ? (
            <div style={st.empty}><div style={{ fontSize: 36, marginBottom: 8, opacity: 0.5 }}>👈</div>일정을 선택하세요</div>
          ) : tab === 'info' ? (
            <InfoTab
              selected={selected} canEdit={canEdit}
              onCopySchedule={onCopySchedule} onDelete={handleDelete}
              onSave={saveSchedule}
              recordCount={attendances.length + guestApps.length}
              attendingCount={presentCount}
            />
          ) : tab === 'attendance' ? (
            <AttendanceTab
              rows={rosterRows} counts={counts} canEdit={canEdit} canApproveGuest={isAdmin}
              loading={attLoading || membersLoading} onSetStatus={setStatus} onSetRsvp={setRsvp}
              onApproveGuest={approveGuest}
              onReload={() => { loadAttendances(selected.id); loadMembers(); }}
            />
          ) : tab === 'lineup' ? (
            <LineupTab
              lineup={lineup} rows={rosterRows} canEdit={canEdit} dirty={lineupDirty}
              saving={lineupSaving} onAssign={assignSlot} onSave={saveLineup}
            />
          ) : tab === 'duty' ? (
            <DutyTab
              duties={duties} rows={rosterRows} canEdit={canEdit}
              newDutyType={newDutyType} setNewDutyType={setNewDutyType}
              onAdd={addDuty} onAssign={assignDuty} onRemove={removeDuty}
            />
          ) : (
            <ProgramTab program={program} items={programItems} />
          )}
        </div>
      </div>

      {/* ── 3열: 미디어 ── */}
      <div style={st.panel}>
        <div style={st.panelHeader}>
          <h2 style={st.panelTitle}>📷 미디어 {selected ? `(${mediaItems.length})` : ''}</h2>
          {selected && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => loadScheduleMedia(selected.id)} disabled={mediaLoading}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 13, cursor: 'pointer', lineHeight: 1 }}
                title="새로고침">
                {mediaLoading ? '⏳' : '🔄'}
              </button>
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                style={{ padding: '5px 10px', borderRadius: 6, border: 'none', backgroundColor: '#5BC4D8', color: '#0A1220', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                {uploading ? '업로드 중...' : '📤 업로드'}
              </button>
              <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" style={{ display: 'none' }}
                onChange={e => handleUpload(e.target.files)} />
            </div>
          )}
        </div>
        <div style={st.panelBody}>
          {!selected ? (
            <div style={st.empty}><div style={{ fontSize: 36, marginBottom: 8, opacity: 0.5 }}>📷</div>일정을 선택하면<br />미디어가 표시됩니다</div>
          ) : (
            <>
              {uploading && (
                <div style={{ padding: 8, marginBottom: 8, borderRadius: 6, backgroundColor: 'rgba(91,196,216,0.05)' }}>
                  <div style={{ fontSize: 10, color: '#AAB4C0', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    📄 {uploadFileName} ({uploadFileIndex.current}/{uploadFileIndex.total})
                  </div>
                  <div style={{ height: 3, backgroundColor: 'rgba(91,196,216,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: uploadProgress + '%', backgroundColor: '#5BC4D8', borderRadius: 2, transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}

              {selectedMedia && (
                <div style={{ marginBottom: 10, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(91,196,216,0.15)' }}>
                  {selectedMedia.type === 'photo' ? (
                    <img src={toWebUrl(selectedMedia.url)} alt={selectedMedia.fileName} style={{ width: '100%', maxHeight: 220, objectFit: 'contain', backgroundColor: '#000', display: 'block' }} />
                  ) : (
                    <video src={toWebUrl(selectedMedia.url)} controls style={{ width: '100%', maxHeight: 220, display: 'block', backgroundColor: '#000' }} />
                  )}
                  <div style={{ padding: '6px 8px', fontSize: 11, color: '#8899AA', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{selectedMedia.fileName}</span>
                    <span>{formatSize(selectedMedia.fileSize)}{selectedMedia.duration ? ` · ${formatDuration(selectedMedia.duration)}` : ''}</span>
                  </div>
                </div>
              )}

              {mediaLoading ? <div style={{ fontSize: 12, color: '#667788', padding: 8, textAlign: 'center' }}>로딩 중...</div> :
                mediaItems.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px 10px', color: '#556677', fontSize: 12 }}>
                    이 일정에 등록된 미디어가 없습니다<br />
                    <span style={{ fontSize: 11, color: '#445566' }}>사진/동영상을 업로드하세요</span>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                    {mediaItems.map(item => (
                      <div key={item.id}
                        onClick={() => setSelectedMedia(selectedMedia?.id === item.id ? null : item)}
                        style={{
                          borderRadius: 6, overflow: 'hidden', position: 'relative', cursor: 'pointer',
                          border: selectedMedia?.id === item.id ? '2px solid #5BC4D8' : '2px solid transparent',
                        }}>
                        {item.type === 'photo' ? (
                          <img src={toWebUrl(item.thumbnailUrl || item.url)} alt={item.fileName}
                            style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block', backgroundColor: '#0d1e2d' }} />
                        ) : item.thumbnailUrl ? (
                          <div style={{ position: 'relative', width: '100%', height: 100 }}>
                            <img src={toWebUrl(item.thumbnailUrl)} alt={item.fileName}
                              style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block', backgroundColor: '#0d1e2d' }} />
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 22, color: '#fff', opacity: 0.85, textShadow: '0 2px 6px rgba(0,0,0,0.5)' }}>▶</div>
                            {item.duration && (
                              <span style={{ position: 'absolute', bottom: 3, right: 4, fontSize: 9, color: '#fff', backgroundColor: 'rgba(0,0,0,0.6)', padding: '1px 4px', borderRadius: 3 }}>
                                {formatDuration(item.duration)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div style={{ width: '100%', height: 100, backgroundColor: '#0d1e2d', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                            <span style={{ fontSize: 22 }}>🎬</span>
                            <span style={{ fontSize: 9, color: '#667788', marginTop: 2 }}>{formatSize(item.fileSize)}</span>
                          </div>
                        )}
                        <div style={{ padding: '3px 5px', backgroundColor: 'rgba(0,0,0,0.4)', position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                          <div style={{ fontSize: 8, color: '#ccc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.fileName}</div>
                        </div>
                        {(canEdit || item.uploadedBy === auth.currentUser?.uid) && (
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteMedia(item.id); }}
                            style={{ position: 'absolute', top: 3, right: 3, width: 18, height: 18, borderRadius: '50%', border: 'none', backgroundColor: 'rgba(0,0,0,0.5)', color: '#F87171', fontSize: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )
              }
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 탭: 정보 ──────────────────────────────────────────────────────────────
// 실제 스키마의 정식 유형만 노출 (레거시 키는 표시용으로만 유지)
const CANONICAL_EVENT_TYPES = ['training', 'regular_game', 'practice_game', 'training_with_game', 'meeting', 'other'];
const GAME_TYPES = ['regular_game', 'practice_game', 'training_with_game', 'game', 'tournament'];

const tsToInput = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function InfoTab({ selected, canEdit, onCopySchedule, onDelete, onSave, recordCount, attendingCount }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rinks, setRinks] = useState([]);
  const [form, setForm] = useState(null);

  // 다른 일정을 고르면 편집 모드 해제
  useEffect(() => { setEditing(false); }, [selected.id]);

  const startEdit = async () => {
    setForm({
      title: selected.title || '',
      status: scheduleStatusOf(selected),
      eventType: selected.eventType || 'training',
      date: tsToInput(selected.date),
      endDate: tsToInput(selected.endDate),
      location: selected.location || '',
      fee: selected.fee != null ? String(selected.fee) : '',
      opponentTeamName: selected.opponentTeamName || '',
      description: selected.description || '',
    });
    setEditing(true);
    if (rinks.length === 0) {
      try {
        const snap = await getDocs(collection(db, 'iceRinks'));
        setRinks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch { /* 링크 목록 없이도 직접 입력 가능 */ }
    }
  };

  const change = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) return alert('제목을 입력하세요.');
    if (!form.date) return alert('시작 일시를 입력하세요.');

    const timeChanged = form.date !== tsToInput(selected.date) || form.endDate !== tsToInput(selected.endDate);

    if (timeChanged && recordCount > 0) {
      const ok = window.confirm(
        `이 일정에는 이미 참가·출석 정보가 있습니다.\n` +
        `(기록 ${recordCount}건, 현재 참석 ${attendingCount}명)\n\n` +
        `일시를 변경하면 이미 응답한 인원의 참석 여부가 실제와 달라질 수 있고,\n` +
        `앱 알림을 받은 인원과 정보가 어긋날 수 있습니다.\n\n` +
        `그래도 변경하시겠습니까?`
      );
      if (!ok) return;
    }

    setSaving(true);
    const payload = {
      title: form.title.trim(),
      status: form.status,
      eventType: form.eventType,
      date: Timestamp.fromDate(new Date(form.date)),
      endDate: form.endDate ? Timestamp.fromDate(new Date(form.endDate)) : null,
      location: form.location,
      fee: form.fee ? parseInt(form.fee, 10) : 0,
      opponentTeamName: form.opponentTeamName || null,
      description: form.description,
    };
    const ok = await onSave(payload);
    setSaving(false);
    if (ok) setEditing(false);
  };

  // ── 편집 모드 ──
  if (editing && form) {
    return (
      <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <EditField label="제목 *">
          <input style={ed.input} value={form.title} onChange={e => change('title', e.target.value)} placeholder="예: 1부 Zone E - 자율훈련" />
        </EditField>

        <EditField label="공개 상태">
          <div style={{ display: 'flex', gap: 4 }}>
            {Object.values(ScheduleStatuses).map(o => {
              const on = form.status === o.value;
              return (
                <button key={o.value} type="button" onClick={() => change('status', o.value)} title={o.desc}
                  style={{
                    flex: 1, padding: '6px 4px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    border: `1px solid ${on ? o.color : 'rgba(255,255,255,0.07)'}`,
                    backgroundColor: on ? `${o.color}1F` : 'transparent',
                    color: on ? o.color : '#667788',
                  }}>{o.icon} {o.label}</button>
              );
            })}
          </div>
          <div style={{ fontSize: 10, color: '#667788', marginTop: 4 }}>
            {ScheduleStatuses[form.status]?.desc}
          </div>
        </EditField>

        <EditField label="유형">
          <select style={ed.input} value={form.eventType} onChange={e => change('eventType', e.target.value)}>
            {CANONICAL_EVENT_TYPES.map(k => <option key={k} value={k}>{EventTypes[k]?.label || k}</option>)}
            {!CANONICAL_EVENT_TYPES.includes(form.eventType) && (
              <option value={form.eventType}>{EventTypes[form.eventType]?.label || form.eventType} (기존값)</option>
            )}
          </select>
        </EditField>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <EditField label="시작 일시 *">
            <input type="datetime-local" style={ed.input} value={form.date} onChange={e => change('date', e.target.value)} />
          </EditField>
          <EditField label="종료 일시">
            <input type="datetime-local" style={ed.input} value={form.endDate} onChange={e => change('endDate', e.target.value)} />
          </EditField>
        </div>

        {recordCount > 0 && (
          <div style={{ padding: '8px 10px', borderRadius: 6, backgroundColor: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)', fontSize: 11, color: '#FBBF24', lineHeight: 1.5 }}>
            ⚠️ 이 일정에는 참가·출석 기록 {recordCount}건이 있습니다. 일시를 바꾸면 저장 시 다시 확인합니다.
          </div>
        )}

        <EditField label="장소">
          <input style={ed.input} list="rink-options" value={form.location}
            onChange={e => change('location', e.target.value)} placeholder="아이스링크 선택 또는 직접 입력" />
          <datalist id="rink-options">
            {rinks.map(r => <option key={r.id} value={r.name} />)}
          </datalist>
        </EditField>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <EditField label="참가비 (원)">
            <input type="number" style={ed.input} value={form.fee} onChange={e => change('fee', e.target.value)} placeholder="0" />
          </EditField>
          {GAME_TYPES.includes(form.eventType) && (
            <EditField label="상대팀">
              <input style={ed.input} value={form.opponentTeamName} onChange={e => change('opponentTeamName', e.target.value)} placeholder="상대팀 이름" />
            </EditField>
          )}
        </div>

        <EditField label="메모">
          <textarea style={{ ...ed.input, minHeight: 70, resize: 'vertical' }} value={form.description}
            onChange={e => change('description', e.target.value)} placeholder="추가 안내사항" />
        </EditField>

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '8px 20px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 700,
            backgroundColor: '#5BC4D8', color: '#0A1220', cursor: 'pointer',
          }}>{saving ? '저장 중...' : '💾 저장'}</button>
          <button onClick={() => setEditing(false)} disabled={saving} style={{
            padding: '8px 16px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)',
            backgroundColor: 'transparent', color: '#8899AA', fontSize: 12, cursor: 'pointer',
          }}>취소</button>
        </div>
      </div>
    );
  }

  // ── 조회 모드 ──
  const curStatus = scheduleStatusOf(selected);
  const cs = ScheduleStatuses[curStatus];

  const quickStatus = async (next) => {
    if (next === curStatus) return;
    if (next === 'cancelled') {
      if (!window.confirm('이 일정을 취소 상태로 바꾸시겠습니까?\n기록은 보존되며 목록에 취소선으로 남습니다.')) return;
    }
    if (next === 'draft' && recordCount > 0) {
      if (!window.confirm(
        `이미 참가·출석 기록 ${recordCount}건이 있습니다.\n` +
        `초안으로 되돌리면 멤버가 응답할 수 없게 됩니다.\n\n계속하시겠습니까?`
      )) return;
    }
    await onSave({ status: next });
  };

  return (
    <div style={{ padding: 8 }}>
      {/* 공개 상태 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
        padding: '10px 12px', borderRadius: 8,
        backgroundColor: `${cs.color}0D`, border: `1px solid ${cs.color}33`,
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: cs.color, flexShrink: 0 }}>{cs.icon} {cs.label}</span>
        <span style={{ fontSize: 11, color: '#778899', flex: 1 }}>{cs.desc}</span>
        {canEdit && (
          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
            {Object.values(ScheduleStatuses).filter(o => o.value !== curStatus).map(o => (
              <button key={o.value} onClick={() => quickStatus(o.value)} title={o.desc}
                style={{
                  padding: '4px 9px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${o.color}44`, backgroundColor: 'transparent', color: o.color,
                }}>{withRo(o.label)}</button>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <InfoBox label="날짜" value={formatDate(selected.date)} />
        <InfoBox label="시간" value={formatTime(selected.date) + (selected.endDate ? ` ~ ${formatTime(selected.endDate)}` : '')} />
        <InfoBox label="장소" value={selected.location || '-'} />
        <InfoBox label="참가비" value={selected.fee ? `${selected.fee.toLocaleString()}원` : '-'} />
        <InfoBox label="유형" value={EventTypes[selected.eventType]?.label || selected.eventType || '-'} />
        {selected.opponentTeamName && <InfoBox label="상대팀" value={selected.opponentTeamName} />}
      </div>
      {selected.description && (
        <div style={{ padding: 10, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.02)', marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#667788', marginBottom: 4, fontWeight: 600 }}>메모</div>
          <div style={{ fontSize: 13, color: '#AAB4C0', lineHeight: 1.6 }}>{selected.description}</div>
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={startEdit}
            style={{ padding: '7px 16px', borderRadius: 6, border: 'none', backgroundColor: '#5BC4D8', color: '#0A1220', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
            ✏️ 일정 수정
          </button>
          {onCopySchedule && (
            <button onClick={() => onCopySchedule(selected)}
              style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.3)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 11, cursor: 'pointer' }}>
              📋 일정 복사
            </button>
          )}
          <button onClick={() => onDelete(selected.id)}
            style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid rgba(248,113,113,0.3)', backgroundColor: 'transparent', color: '#F87171', fontSize: 11, cursor: 'pointer' }}>
            🗑️ 일정 삭제
          </button>
        </div>
      )}
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: '#8899AA', fontWeight: 600, display: 'block', marginBottom: 4, letterSpacing: 0.3 }}>{label}</label>
      {children}
    </div>
  );
}

const ed = {
  input: {
    width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
    border: '1px solid rgba(91,196,216,0.12)', backgroundColor: 'rgba(10,18,32,0.8)',
    color: '#E8ECF1', boxSizing: 'border-box', outline: 'none',
  },
};

// ── 탭: 참석 ──────────────────────────────────────────────────────────────

// 정렬 기준
const SORT_OPTIONS = [
  { key: 'status',   label: '상태순' },
  { key: 'name',     label: '이름순' },
  { key: 'jersey',   label: '등번호순' },
  { key: 'rsvp',     label: 'RSVP순' },
  { key: 'position', label: '포지션순' },
  { key: 'checkin',  label: '체크인순' },
];
const RSVP_ORDER = { will_attend: 0, undecided: 1, will_not_attend: 2 };
const POSITION_ORDER = { LW: 0, C: 1, RW: 2, F: 3, LD: 4, RD: 5, D: 6, G: 7 };
const ROLE_FILTERS = [
  { key: 'all', label: '전체 역할' },
  { key: 'coach', label: '코치' },
  { key: 'admin', label: '운영진' },
  { key: 'member', label: '멤버' },
  { key: 'guest', label: '게스트' },
];

const GuestStatuses = {
  pending:   { label: '승인 대기', color: '#FBBF24' },
  approved:  { label: '승인됨',   color: '#4ADE80' },
  confirmed: { label: '확정',     color: '#5BC4D8' },
  waitlisted:{ label: '대기자',   color: '#F97316' },
  rejected:  { label: '거절됨',   color: '#F87171' },
  cancelled: { label: '취소됨',   color: '#9CA3AF' },
};

// 무효 게스트 (거절/취소) — 명단에는 남기되 집계와 편집에서 제외
const isInactiveGuest = (r) => !!(r.guest && ['rejected', 'cancelled'].includes(r.guest.status));

// 본인 응답이 아니라 운영진이 대신 기록한 RSVP인지
const isProxy = (r) => !!(r.att?.rsvpSetBy && r.att.rsvpSetBy !== r.userId);
const statusOf = (r) => (r.att?.status && STATUS_ORDER[r.att.status] !== undefined) ? r.att.status : 'undecided';
const rsvpOf = (r) => r.att?.rsvpResponse || 'none';

function AttendanceTab({ rows, counts, canEdit, canApproveGuest, loading, onSetStatus, onSetRsvp, onApproveGuest, onReload }) {
  const [sortKey, setSortKey] = useState(() => localStorage.getItem('ionice_att_sortKey') || 'status');
  const [asc, setAsc] = useState(() => localStorage.getItem('ionice_att_sortAsc') !== 'false');
  const [statusFilter, setStatusFilter] = useState([]);   // 빈 배열 = 전체
  const [rsvpFilter, setRsvpFilter] = useState([]);
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(true);

  const changeSort = (k) => { setSortKey(k); localStorage.setItem('ionice_att_sortKey', k); };
  const toggleAsc = () => { const v = !asc; setAsc(v); localStorage.setItem('ionice_att_sortAsc', String(v)); };
  const toggleIn = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  const clearAll = () => { setStatusFilter([]); setRsvpFilter([]); setRoleFilter('all'); setSearch(''); };

  // 필터별 카운트 (필터 적용 전 기준)
  const countBase = rows.filter(r => !isInactiveGuest(r));
  const statusCount = {};
  Object.keys(STATUS_ORDER).forEach(s => { statusCount[s] = countBase.filter(r => statusOf(r) === s).length; });
  const rsvpCount = {
    will_attend: countBase.filter(r => rsvpOf(r) === 'will_attend').length,
    will_not_attend: countBase.filter(r => rsvpOf(r) === 'will_not_attend').length,
    none: countBase.filter(r => rsvpOf(r) === 'none' || rsvpOf(r) === 'undecided').length,
  };
  const inactiveCount = rows.length - countBase.length;

  let visible = rows.filter(r => {
    if (!showInactive && isInactiveGuest(r)) return false;
    if (statusFilter.length > 0 && !statusFilter.includes(statusOf(r))) return false;
    if (rsvpFilter.length > 0) {
      const rv = rsvpOf(r);
      const norm = (rv === 'undecided') ? 'none' : rv;
      if (!rsvpFilter.includes(norm)) return false;
    }
    if (roleFilter !== 'all') {
      if (roleFilter === 'guest' ? !r.isGuest : (r.isGuest || r.role !== roleFilter)) return false;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${r.name || ''} ${r.jerseyNumber ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  visible = visible.sort((a, b) => {
    // 거절·취소 게스트는 정렬 기준과 무관하게 항상 맨 아래
    const ia = isInactiveGuest(a) ? 1 : 0;
    const ib = isInactiveGuest(b) ? 1 : 0;
    if (ia !== ib) return ia - ib;

    let v = 0;
    switch (sortKey) {
      case 'name':
        v = (a.name || '').localeCompare(b.name || '', 'ko'); break;
      case 'jersey':
        v = (a.jerseyNumber ?? 99999) - (b.jerseyNumber ?? 99999); break;
      case 'rsvp':
        v = (RSVP_ORDER[a.att?.rsvpResponse] ?? 1) - (RSVP_ORDER[b.att?.rsvpResponse] ?? 1); break;
      case 'position':
        v = (POSITION_ORDER[a.position] ?? 99) - (POSITION_ORDER[b.position] ?? 99); break;
      case 'checkin': {
        const ta = a.att?.checkInTime?.seconds ?? a.att?.rsvpRespondedAt?.seconds ?? Infinity;
        const tb = b.att?.checkInTime?.seconds ?? b.att?.rsvpRespondedAt?.seconds ?? Infinity;
        v = ta === tb ? 0 : (ta < tb ? -1 : 1); break;
      }
      default:
        v = (STATUS_ORDER[statusOf(a)] ?? 3) - (STATUS_ORDER[statusOf(b)] ?? 3);
    }
    if (v === 0) v = (a.name || '').localeCompare(b.name || '', 'ko');
    return asc ? v : -v;
  });

  const guestTotal = countBase.filter(r => r.guest).length;
  const guestPending = countBase.filter(r => r.guest?.status === 'pending').length;
  const filterActive = statusFilter.length > 0 || rsvpFilter.length > 0 || roleFilter !== 'all' || search.trim();

  return (
    <div style={{ padding: 4 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <StatBox label="참석" count={counts.attending} color="#4ADE80" />
        <StatBox label="지각" count={counts.late} color="#FBBF24" />
        <StatBox label="불참" count={counts.not_attending} color="#F87171" />
        <StatBox label="미정" count={counts.undecided} color="#9CA3AF" />
      </div>

      {/* ── 검색 + 정렬 ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="이름 · 등번호 검색"
          style={{
            flex: 1, padding: '6px 10px', borderRadius: 6, fontSize: 12,
            backgroundColor: '#0F1B2C', color: '#E8ECF1',
            border: '1px solid rgba(91,196,216,0.15)', outline: 'none',
          }}
        />
        <select value={sortKey} onChange={e => changeSort(e.target.value)} style={{ ...st.select, width: 'auto', fontSize: 12, padding: '6px 8px' }}>
          {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <button onClick={toggleAsc} title={asc ? '오름차순' : '내림차순'}
          style={{
            padding: '6px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 700,
            border: '1px solid rgba(91,196,216,0.15)', backgroundColor: 'transparent', color: '#5BC4D8',
          }}>
          {asc ? '↑' : '↓'}
        </button>
      </div>

      {/* ── 상태 필터 ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={st.filterLabel}>상태</span>
        {['attending', 'late', 'early_leave', 'not_attending', 'undecided'].map(s => {
          const meta = AttendanceStatuses[s];
          const on = statusFilter.includes(s);
          return (
            <button key={s} onClick={() => toggleIn(statusFilter, setStatusFilter, s)} style={{
              ...st.chip,
              border: `1px solid ${on ? meta.color : 'rgba(255,255,255,0.06)'}`,
              backgroundColor: on ? `${meta.color}1F` : 'transparent',
              color: on ? meta.color : '#667788',
            }}>
              {meta.label} {statusCount[s] || 0}
            </button>
          );
        })}
      </div>

      {/* ── RSVP + 역할 필터 ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={st.filterLabel}>RSVP</span>
        {[
          { k: 'will_attend', label: '참석 예정', color: '#4ADE80', n: rsvpCount.will_attend },
          { k: 'will_not_attend', label: '불참 예정', color: '#F87171', n: rsvpCount.will_not_attend },
          { k: 'none', label: '무응답', color: '#9CA3AF', n: rsvpCount.none },
        ].map(o => {
          const on = rsvpFilter.includes(o.k);
          return (
            <button key={o.k} onClick={() => toggleIn(rsvpFilter, setRsvpFilter, o.k)} style={{
              ...st.chip,
              border: `1px solid ${on ? o.color : 'rgba(255,255,255,0.06)'}`,
              backgroundColor: on ? `${o.color}1F` : 'transparent',
              color: on ? o.color : '#667788',
            }}>
              {o.label} {o.n}
            </button>
          );
        })}
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ ...st.select, width: 'auto', fontSize: 11, padding: '4px 6px', marginLeft: 4 }}>
          {ROLE_FILTERS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>

      {/* ── 결과 요약 ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, padding: '0 4px' }}>
        <span style={{ fontSize: 11, color: '#667788' }}>
          {filterActive ? `${visible.length}명 / 전체 ${countBase.length}명` : `전체 ${countBase.length}명`}
          {guestTotal > 0 && (
            <span style={{ marginLeft: 8, color: '#F97316' }}>
              게스트 {guestTotal}명{guestPending > 0 ? ` (대기 ${guestPending})` : ''}
            </span>
          )}
          {inactiveCount > 0 && (
            <button onClick={() => setShowInactive(v => !v)} style={{
              marginLeft: 8, padding: '2px 8px', borderRadius: 5, border: 'none', fontSize: 10, cursor: 'pointer',
              backgroundColor: showInactive ? 'rgba(255,255,255,0.05)' : 'transparent', color: '#667788',
            }}>
              {showInactive ? `취소·거절 ${inactiveCount} 숨기기` : `취소·거절 ${inactiveCount} 보기`}
            </button>
          )}
          {filterActive && (
            <button onClick={clearAll} style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 5, border: 'none', backgroundColor: 'rgba(255,255,255,0.05)', color: '#8899AA', fontSize: 10, cursor: 'pointer' }}>
              필터 해제
            </button>
          )}
        </span>
        <button onClick={onReload} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 11, cursor: 'pointer' }}>
          🔄 새로고침
        </button>
      </div>

      {loading ? <div style={st.empty}>로딩 중...</div> : visible.length === 0 ? (
        <div style={st.empty}>조건에 맞는 인원이 없습니다</div>
      ) : visible.map(row => {
        const status = statusOf(row);
        const si = AttendanceStatuses[status] || AttendanceStatuses.undecided;
        const inactive = isInactiveGuest(row);
        return (
          <div key={row.userId} style={{ ...st.attRow, opacity: inactive ? 0.45 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <div style={{ ...st.avatar, border: `1px solid ${si.color}44` }}>{(row.name || '?')[0].toUpperCase()}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: inactive ? 'line-through' : 'none' }}>{row.name}</span>
                  {row.jerseyNumber != null && <span style={{ fontSize: 10, color: '#5BC4D8' }}>#{row.jerseyNumber}</span>}
                  {row.isGuest && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, backgroundColor: 'rgba(249,115,22,0.15)', color: '#F97316', fontWeight: 700 }}>게스트</span>}
                  {row.guest && (() => {
                    const gs = GuestStatuses[row.guest.status] || GuestStatuses.pending;
                    return (
                      <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, border: `1px solid ${gs.color}66`, color: gs.color, fontWeight: 700 }}>
                        {gs.label}
                      </span>
                    );
                  })()}
                  {row.guest?.paymentStatus === 'paid' && (
                    <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, backgroundColor: 'rgba(74,222,128,0.12)', color: '#4ADE80', fontWeight: 700 }}>결제완료</span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: '#667788' }}>
                  {positionLabel(row.position, row.positions)}
                  {row.att?.rsvpResponse && (
                    <span style={{ marginLeft: row.position ? 6 : 0, color: RSVPResponses[row.att.rsvpResponse]?.color || '#9CA3AF' }}>
                      RSVP {RSVPResponses[row.att.rsvpResponse]?.label || row.att.rsvpResponse}
                      {isProxy(row) && (
                        <span style={{ marginLeft: 4, color: '#8B5CF6' }}>
                          · 대리{row.att.rsvpSetByName ? `(${row.att.rsvpSetByName})` : ''}
                        </span>
                      )}
                    </span>
                  )}
                  {row.att?.isSelfCheckIn && <span style={{ marginLeft: 6, color: '#4ADE80' }}>셀프 체크인</span>}
                  {row.guest?.message && (
                    <span style={{ marginLeft: 6, color: '#7788AA' }}>💬 {row.guest.message}</span>
                  )}
                </div>
              </div>
            </div>

            {inactive ? (
              <span style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 10, flexShrink: 0, fontWeight: 600,
                border: `1px solid ${(GuestStatuses[row.guest.status] || GuestStatuses.cancelled).color}`,
                color: (GuestStatuses[row.guest.status] || GuestStatuses.cancelled).color,
              }}>
                {(GuestStatuses[row.guest.status] || GuestStatuses.cancelled).label}
              </span>
            ) : canApproveGuest ? (
              <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                {row.guest?.status === 'pending' && (
                  <button onClick={() => onApproveGuest(row.guest, row.name)} style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    border: 'none', backgroundColor: '#FBBF24', color: '#0A1220',
                  }}>승인</button>
                )}

                {/* RSVP 대리 입력 */}
                <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  <span style={{ fontSize: 9, color: '#445566', fontWeight: 700, marginRight: 1 }}>RSVP</span>
                  {[
                    { k: 'will_attend', label: '예정', color: '#4ADE80' },
                    { k: 'will_not_attend', label: '불참', color: '#F87171' },
                  ].map(o => {
                    const active = row.att?.rsvpResponse === o.k;
                    return (
                      <button key={o.k} onClick={() => onSetRsvp(row, o.k)}
                        title="외부(카톡 등)로 확인된 응답을 대신 기록합니다. 다시 누르면 해제"
                        style={{
                          padding: '3px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                          border: `1px dashed ${active ? o.color : 'rgba(255,255,255,0.08)'}`,
                          backgroundColor: active ? `${o.color}18` : 'transparent',
                          color: active ? o.color : '#556677',
                        }}>{o.label}</button>
                    );
                  })}
                </div>

                <span style={{ width: 1, height: 18, backgroundColor: 'rgba(255,255,255,0.06)' }} />

                {/* 출석 확정 */}
                <div style={{ display: 'flex', gap: 3 }}>
                  {['attending', 'late', 'not_attending'].map(s => {
                    const meta = AttendanceStatuses[s];
                    const active = status === s;
                    return (
                      <button key={s} onClick={() => onSetStatus(row, s)} style={{
                        padding: '4px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        border: `1px solid ${active ? meta.color : 'rgba(91,196,216,0.1)'}`,
                        backgroundColor: active ? `${meta.color}20` : 'transparent',
                        color: active ? meta.color : '#667788',
                      }}>{meta.label}</button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, border: `1px solid ${si.color}`, color: si.color, fontWeight: 600, flexShrink: 0 }}>
                {si.label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── 탭: 라인업 ────────────────────────────────────────────────────────────
function LineupTab({ lineup, rows, canEdit, dirty, saving, onAssign, onSave }) {
  if (!lineup) return <div style={st.empty}>로딩 중...</div>;

  // 참석자 우선 정렬된 후보 목록
  const candidates = [...rows].sort((a, b) => {
    const pa = PRESENT.includes(a.att?.status) ? 0 : 1;
    const pb = PRESENT.includes(b.att?.status) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    return (a.name || '').localeCompare(b.name || '', 'ko');
  });

  const assignedIds = lineup.slots.filter(s => s.playerId).map(s => s.playerId);
  const unassignedPresent = rows.filter(r => PRESENT.includes(r.att?.status) && !assignedIds.includes(r.userId));

  const renderLine = (lineType, lineNumber) => {
    const slots = lineup.slots.filter(s => s.lineType === lineType && s.lineNumber === lineNumber);
    if (slots.length === 0) return null;
    const labels = { forward: `${lineNumber}라인`, defense: `D${lineNumber}`, goalie: `G${lineNumber}` };
    return (
      <div key={`${lineType}-${lineNumber}`} style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: '#667788', fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>{labels[lineType]}</div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${slots.length}, 1fr)`, gap: 6 }}>
          {slots.map(slot => (
            <div key={slot.id} style={{
              padding: 8, borderRadius: 8,
              backgroundColor: slot.playerId ? 'rgba(91,196,216,0.06)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${slot.playerId ? 'rgba(91,196,216,0.2)' : 'rgba(255,255,255,0.04)'}`,
            }}>
              <div style={{ fontSize: 10, color: '#5BC4D8', fontWeight: 700, marginBottom: 4 }}>{slot.position}</div>
              {canEdit ? (
                <select value={slot.playerId || ''} onChange={e => onAssign(slot.id, e.target.value)} style={st.select}>
                  <option value="">— 비어 있음 —</option>
                  {candidates.map(c => (
                    <option key={c.userId} value={c.userId}>
                      {PRESENT.includes(c.att?.status) ? '✅ ' : '· '}{c.name}{c.jerseyNumber != null ? ` #${c.jerseyNumber}` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ fontSize: 12, color: slot.playerName ? '#E8ECF1' : '#556677' }}>
                  {slot.playerName || '—'}{slot.playerNumber != null ? ` #${slot.playerNumber}` : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 11, color: '#667788' }}>
          배정 {assignedIds.length}명 · 참석자 중 미배정 {unassignedPresent.length}명
        </span>
        {canEdit && (
          <button onClick={onSave} disabled={!dirty || saving} style={{
            padding: '5px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 700,
            cursor: dirty ? 'pointer' : 'default',
            backgroundColor: dirty ? '#5BC4D8' : 'rgba(255,255,255,0.05)',
            color: dirty ? '#0A1220' : '#556677',
          }}>
            {saving ? '저장 중...' : dirty ? '💾 저장' : '저장됨'}
          </button>
        )}
      </div>

      <div style={{ fontSize: 11, color: '#5BC4D8', fontWeight: 700, margin: '10px 0 6px' }}>FORWARD</div>
      {[1, 2, 3, 4].map(n => renderLine('forward', n))}
      <div style={{ fontSize: 11, color: '#5BC4D8', fontWeight: 700, margin: '14px 0 6px' }}>DEFENSE</div>
      {[1, 2, 3].map(n => renderLine('defense', n))}
      <div style={{ fontSize: 11, color: '#5BC4D8', fontWeight: 700, margin: '14px 0 6px' }}>GOALIE</div>
      {[1, 2].map(n => renderLine('goalie', n))}

      {unassignedPresent.length > 0 && (
        <div style={{ marginTop: 14, padding: 10, borderRadius: 8, backgroundColor: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.15)' }}>
          <div style={{ fontSize: 11, color: '#FBBF24', fontWeight: 700, marginBottom: 6 }}>참석하는데 라인에 없는 인원</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {unassignedPresent.map(r => (
              <span key={r.userId} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.04)', color: '#AAB4C0' }}>
                {r.name}{r.jerseyNumber != null ? ` #${r.jerseyNumber}` : ''}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── 탭: 당번 ──────────────────────────────────────────────────────────────
function DutyTab({ duties, rows, canEdit, newDutyType, setNewDutyType, onAdd, onAssign, onRemove }) {
  const candidates = [...rows].sort((a, b) => {
    const pa = PRESENT.includes(a.att?.status) ? 0 : 1;
    const pb = PRESENT.includes(b.att?.status) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    return (a.name || '').localeCompare(b.name || '', 'ko');
  });

  return (
    <div style={{ padding: 4 }}>
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <select value={newDutyType} onChange={e => setNewDutyType(e.target.value)} style={{ ...st.select, flex: 1 }}>
            {Object.values(DutyPresets).map(p => (
              <option key={p.code} value={p.code}>{p.icon} {p.label}</option>
            ))}
          </select>
          <button onClick={onAdd} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', backgroundColor: '#5BC4D8', color: '#0A1220', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
            + 추가
          </button>
        </div>
      )}

      {duties.length === 0 ? (
        <div style={st.empty}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>📌</div>
          등록된 당번이 없습니다
        </div>
      ) : duties.map(d => {
        const meta = dutyLabel(d);
        const si = DutyStatuses[d.status] || DutyStatuses.unassigned;
        return (
          <div key={d.id} style={{ ...st.attRow, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span style={{ fontSize: 18 }}>{meta.icon}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{meta.label}</div>
                <div style={{ fontSize: 10, color: si.color }}>{d.assignedUserName || si.label}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
              {canEdit && (
                <>
                  <select value={d.assignedUserId || ''} onChange={e => onAssign(d.id, e.target.value)} style={{ ...st.select, minWidth: 130 }}>
                    <option value="">— 미지정 —</option>
                    {candidates.map(c => (
                      <option key={c.userId} value={c.userId}>
                        {PRESENT.includes(c.att?.status) ? '✅ ' : '· '}{c.name}
                      </option>
                    ))}
                  </select>
                  <button onClick={() => onRemove(d.id)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(248,113,113,0.25)', backgroundColor: 'transparent', color: '#F87171', fontSize: 11, cursor: 'pointer' }}>✕</button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── 탭: 세션 프로그램 (읽기 전용) ────────────────────────────────────────
function ProgramTab({ program, items }) {
  if (!program) {
    return (
      <div style={st.empty}>
        <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>📋</div>
        이 일정에 등록된 세션 프로그램이 없습니다<br />
        <span style={{ fontSize: 11, color: '#445566' }}>앱의 코치 모드에서 프로그램을 작성하세요</span>
      </div>
    );
  }
  const total = program.estimatedTotalDuration || program.totalDuration ||
    items.reduce((sum, i) => sum + (i.customDuration ?? i.duration ?? 0), 0);

  return (
    <div style={{ padding: 4 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#5BC4D8', marginBottom: 4 }}>{program.title || '세션 프로그램'}</div>
        {(program.objective || program.description) && (
          <div style={{ fontSize: 12, color: '#AAB4C0', lineHeight: 1.6 }}>{program.objective || program.description}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <Chip text={`총 ${total}분`} color="#5BC4D8" />
          {program.overallIntensity && <Chip text={`강도 ${program.overallIntensity}`} color="#F59E0B" />}
          {program.status && <Chip text={program.status === 'draft' ? '초안' : program.status} color="#9CA3AF" />}
        </div>
      </div>

      {items.length === 0 ? (
        <div style={st.empty}>드릴 항목이 없습니다</div>
      ) : items.map((it, idx) => (
        <div key={it.id || idx} style={{ display: 'flex', gap: 10, padding: '10px 8px', borderBottom: '1px solid rgba(91,196,216,0.04)' }}>
          <div style={{
            width: 24, height: 24, borderRadius: 6, flexShrink: 0,
            backgroundColor: 'rgba(91,196,216,0.12)', color: '#5BC4D8',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
          }}>{idx + 1}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{it.workoutName || it.name || '이름 없음'}</div>
            <div style={{ fontSize: 10, color: '#667788', marginTop: 2 }}>
              {(it.workoutCategory || it.category || 'other')} · {(it.customDuration ?? it.duration ?? 0)}분
              {it.difficulty ? ` · ${it.difficulty}` : ''}
            </div>
            {(it.customNote || it.notes || it.coachNote) && (
              <div style={{ fontSize: 11, color: '#8899AA', marginTop: 4, lineHeight: 1.5 }}>
                {it.customNote || it.notes || it.coachNote}
              </div>
            )}
          </div>
        </div>
      ))}

      {program.coachNotes && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.02)' }}>
          <div style={{ fontSize: 11, color: '#667788', fontWeight: 600, marginBottom: 4 }}>코치 메모</div>
          <div style={{ fontSize: 12, color: '#AAB4C0', lineHeight: 1.6 }}>{program.coachNotes}</div>
        </div>
      )}
    </div>
  );
}

// ── 공통 소품 ─────────────────────────────────────────────────────────────
function ScheduleCard({ schedule, selected, count, onClick }) {
  const et = EventTypes[schedule.eventType] || {};
  const today = isToday(schedule.date, schedule.endDate);
  const stKey = scheduleStatusOf(schedule);
  const ss = ScheduleStatuses[stKey];
  const muted = stKey === 'draft' || stKey === 'cancelled';
  return (
    <div onClick={onClick} style={{
      padding: 10, marginBottom: 4, borderRadius: 6, cursor: 'pointer',
      borderLeft: `3px ${stKey === 'draft' ? 'dashed' : 'solid'} ${today && !muted ? '#EF4444' : (muted ? ss.color : (et.color || '#5BC4D8'))}`,
      backgroundColor: selected?.id === schedule.id ? 'rgba(91,196,216,0.06)' : 'rgba(255,255,255,0.02)',
      opacity: stKey === 'cancelled' ? 0.5 : stKey === 'draft' ? 0.72 : 1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          <span style={{
            fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            textDecoration: stKey === 'cancelled' ? 'line-through' : 'none',
          }}>{schedule.title}</span>
          {today && stKey !== 'cancelled' && <span style={{ fontSize: 8, padding: '1px 5px', borderRadius: 4, backgroundColor: 'rgba(239,68,68,0.15)', color: '#EF4444', fontWeight: 700, flexShrink: 0 }}>오늘</span>}
        </div>
        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
          {stKey !== 'open' && (
            <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, backgroundColor: `${ss.color}1F`, color: ss.color, fontWeight: 700 }}>
              {ss.icon} {ss.label}
            </span>
          )}
          {et.label && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, border: `1px solid ${et.color}`, color: et.color, fontWeight: 600 }}>{et.label}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#778899' }}>
        <span>📆 {formatDate(schedule.date)}</span>
        <span>⏰ {formatTime(schedule.date)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 3 }}>
        {schedule.location ? <span style={{ fontSize: 11, color: '#667788' }}>📍 {schedule.location}</span> : <span />}
        {count && count.present > 0 && (
          <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 8, backgroundColor: 'rgba(74,222,128,0.12)', color: '#4ADE80', fontWeight: 700 }}>
            👥 {count.present}
          </span>
        )}
      </div>
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div style={{ padding: 10, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.02)' }}>
      <div style={{ fontSize: 10, color: '#667788', fontWeight: 600, letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function StatBox({ label, count, color }) {
  return (
    <div style={{ flex: 1, textAlign: 'center', padding: 10, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.02)' }}>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{count}</div>
      <div style={{ fontSize: 10, color: '#778899', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function Chip({ text, color }) {
  return (
    <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 10, border: `1px solid ${color}44`, color, fontWeight: 600 }}>{text}</span>
  );
}

const st = {
  panel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, overflow: 'hidden' },
  panelHeader: { padding: '12px 16px', borderBottom: '1px solid rgba(91,196,216,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  panelTitle: { margin: 0, fontSize: 14, fontWeight: 600 },
  panelBody: { padding: 10, maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' },
  empty: { textAlign: 'center', padding: '40px 16px', color: '#556677', fontSize: 13 },
  filterBtn: { padding: '3px 8px', borderRadius: 5, border: 'none', fontSize: 11, cursor: 'pointer', fontWeight: 500 },
  attRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 6px', borderBottom: '1px solid rgba(91,196,216,0.04)' },
  avatar: { width: 30, height: 30, borderRadius: '50%', backgroundColor: 'rgba(91,196,216,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#5BC4D8', flexShrink: 0 },
  select: {
    width: '100%', padding: '5px 6px', borderRadius: 6, fontSize: 11,
    backgroundColor: '#0F1B2C', color: '#E8ECF1',
    border: '1px solid rgba(91,196,216,0.15)', cursor: 'pointer', outline: 'none',
  },
  chip: { padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  filterLabel: { fontSize: 10, color: '#556677', fontWeight: 700, width: 30, flexShrink: 0 },
};
